import { _ } from "../../../core/polyglot/polyglot.js";
import Model from "../../Model.js";

/**
 * `AgedAccountsSummaryGroup` represents the total aged accounts for a particular
 * period of time.
 * It stores the data necessary to render the group peculiarities for the aged accounts
 * summary table and chart widgets in the PeriodReport.
 *
 * @param {string} label - Renderable of the group's title (e.g., "days 31-60").
 * @param {number} value - Total amount of this group's aged accounts values.
 * @param {string} legend - A small non-user-visible string used to distinguish groups.
 * Depends on the label and value (e.g., "31-60").
 * @param {number} percentage - The amount of value this group represents
 * from the whole aged accounts summary.
 */
export default class AgedAccountsSummaryGroup extends Model {
	constructor({ label, value, summaryTotal }) {
		super(...arguments);

		const hasOverdue = label !== "No overdue";
		// last word of the label (e.g., "days 31-60" => "31-60")
		const daysNumber = label.split(" ").pop();

		this._label = hasOverdue
			? this.constructor.formatLabel(daysNumber)
			: _("On time");

		this._legend = hasOverdue
			? this.constructor.formatLegend(daysNumber)
			: "on-time";

		this._value = value;
		this._percentage = Math.round((value * 100) / summaryTotal);
	}

	getLabel() {
		return this._label;
	}

	getValue() {
		return this._value;
	}

	getLegend() {
		if (this.isNegative()) {
			return "negative";
		}

		return this._legend;
	}

	getPercentage() {
		return this._percentage;
	}

	setPercentage(percentage) {
		this._percentage = percentage;
	}

	isNegative() {
		return this._value < 0;
	}

	static formatLabel(daysNumber) {
		return _("{daysNumber} days", { daysNumber });
	}

	static formatLegend(daysNumber) {
		// removes a symbole that we can't use in css (i.e., for ">365")
		return daysNumber.replace(/>/, "");
	}
}
